/**
 * Parser for the fenced ```lastbot-media``` block the backend emits to render
 * sized media inline. The block body is simple `key: value` lines (one per
 * line, split on the first colon), e.g.:
 *
 *   ```lastbot-media
 *   type: video
 *   src: https://cdn.example.com/clip.mp4
 *   aspect: 16x9
 *   poster: https://cdn.example.com/p.jpg
 *   ```
 *
 * This is NOT a markdown plugin — the fence is standard markdown and the body
 * is literal text, so it never affects how the rest of a message is parsed.
 */

export interface MediaDescriptor {
  type: "image" | "video";
  src: string;
  /** Whitelisted aspect token (16x9 / 4x3 / 3x4 / 1x1) or undefined. */
  aspect?: string;
  /** Optional poster image for video. */
  poster?: string;
  /**
   * Accessible label for the media (image `alt` / video `aria-label`). Parsed
   * from the `alt:` key (or its `label:` alias). Omit for decorative media.
   */
  alt?: string;
}

const ASPECT_TOKENS = new Set(["16x9", "4x3", "3x4", "1x1"]);

/** Only http(s) URLs are allowed for any media source. */
const isHttpUrl = (value: string): boolean => {
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
};

/**
 * Parse + validate a fenced media block body. Returns null if the descriptor is
 * unusable (unknown type, missing/unsafe src) so the caller can render nothing.
 * Unknown keys and invalid optional fields are ignored — forward-compatible.
 */
export const parseMediaBlock = (raw: string): MediaDescriptor | null => {
  const fields: Record<string, string> = {};
  for (const line of raw.split(/\r?\n/)) {
    const idx = line.indexOf(":");
    if (idx === -1) continue;
    const key = line.slice(0, idx).trim().toLowerCase();
    const value = line.slice(idx + 1).trim();
    if (key) fields[key] = value;
  }

  const type = fields.type;
  if (type !== "image" && type !== "video") return null;
  if (!fields.src || !isHttpUrl(fields.src)) return null;

  const aspect =
    fields.aspect && ASPECT_TOKENS.has(fields.aspect) ? fields.aspect : undefined;
  const poster =
    fields.poster && isHttpUrl(fields.poster) ? fields.poster : undefined;
  // Accessible label: `alt:` preferred, `label:` as an alias. Empty → undefined.
  const alt = fields.alt || fields.label || undefined;

  return { type, src: fields.src, aspect, poster, alt };
};
